import { useFeatureFlag } from "configcat-react";
import { useLocation } from "react-router-dom"
import { CONFIGCAT_KEYS } from "../configCat/configCatKeys";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { useCallback, useEffect } from "react";
import { differenceInSeconds } from "date-fns";
import { useTracking } from "../Tracking/TrackingContext";

const REFRESHED_ON_KEY = 'refreshed_on';

export const useVersionChecker = () => {
    const location = useLocation();
    const { TrackFeatureFlagEvent, EventTrack } = useTracking();
    const { datadogConfig, configCat } = useAppSettings();
    const { value: versionCheckerEnabled, loading: versionCheckerEnabledLoading } = useFeatureFlag(
        CONFIGCAT_KEYS.TOGGLE_VERSION_CHECKER.key, CONFIGCAT_KEYS.TOGGLE_VERSION_CHECKER.defaultValue);
    const { value: loanWebVersion, loading: loanWebVersionLoading } = useFeatureFlag(
        CONFIGCAT_KEYS.LOAN_WEB_VERSION.key, CONFIGCAT_KEYS.LOAN_WEB_VERSION.defaultValue);

    // Refresh should align with ConfigCat's Polling interval
    // This is to prevent multiple consecutive refreshes (refresh spiral loop)
    const refreshIntervalElapsed = useCallback(() => {
        const lastRefreshedOn = localStorage.getItem(REFRESHED_ON_KEY);

        if (lastRefreshedOn === null)
            return true;

        const lastRefreshedDate = new Date(JSON.parse(lastRefreshedOn) as string);

        const secondsSinceLastRefresh = differenceInSeconds(new Date(), lastRefreshedDate);

        return secondsSinceLastRefresh >= configCat.pollingInterval
    }, [configCat.pollingInterval])


    const reloadClient = useCallback(() => {
        // From env-config (stale if user hasn't refreshed in a while)
        const currentVersion = datadogConfig.version;

        if (versionCheckerEnabled && loanWebVersion !== currentVersion && refreshIntervalElapsed()) {
            EventTrack('VersionCheckerRefreshed', { value: true });
            console.log(`Version mismatch: current ${currentVersion}, latest ${loanWebVersion}`);
            localStorage.setItem(REFRESHED_ON_KEY, JSON.stringify(new Date()));
            window.location.reload();
        }
    }, [EventTrack, datadogConfig.version, loanWebVersion, refreshIntervalElapsed, versionCheckerEnabled])

    useEffect(() => {
        // Track feature flag
        TrackFeatureFlagEvent({ featureFlag: CONFIGCAT_KEYS.LOAN_WEB_VERSION.key, value: loanWebVersion })

        const isDefaultOrLocal = () => {
            return loanWebVersion === 'release/dev' || datadogConfig.env === 'dev';
        }

        if (versionCheckerEnabledLoading || loanWebVersionLoading || isDefaultOrLocal()) return;

        reloadClient();

    }, [location.pathname, TrackFeatureFlagEvent, datadogConfig.env, loanWebVersion, loanWebVersionLoading, reloadClient, versionCheckerEnabledLoading]);
}